import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, UserPlus, BookOpen, Heart, TrendingUp, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const programs = [
  {
    id: 'recruitment',
    title: 'Recruitment',
    description: 'Increasing the presence of men of color in early childhood classrooms is our primary goal. We actively recruit through strategic partnerships and the DC Leading Men Fellowship.',
    icon: UserPlus,
    image: '/images/event-photo-1.jpg',
    link: '/recruitment',
    color: 'from-brand-gold to-gold-600',
    shadowColor: 'shadow-gold-500/20'
  },
  {
    id: 'development',
    title: 'Development',
    description: 'We are committed to providing our members with the knowledge, skills and dispositions necessary for success through ongoing communication and professional development.',
    icon: BookOpen,
    image: '/images/event-photo-2.jpg',
    link: '/development',
    color: 'from-brand-green to-green-600',
    shadowColor: 'shadow-green-500/20'
  },
  {
    id: 'retention',
    title: 'Retention',
    description: 'Our retention efforts focus on supporting men of color throughout their careers with structured onboarding, mentoring programs, and continuous professional development.',
    icon: Heart,
    image: '/images/retention-photo.jpg',
    link: '/retention',
    color: 'from-brand-red to-red-600',
    shadowColor: 'shadow-red-500/20'
  },
  {
    id: 'advancement',
    title: 'Advancement',
    description: 'We provide opportunities for skill and knowledge development, career mobility, and professional recognition through partnerships with leading educational institutions.',
    icon: TrendingUp,
    image: '/images/advancement-photo.jpg',
    link: '/advancement',
    color: 'from-brand-navy to-navy-700',
    shadowColor: 'shadow-navy-500/20'
  }
];

export default function ProgramsPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-64 h-64 bg-green-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Programs</span>
            </nav>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                Programs
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              DC Men in ECE supports men of color throughout their journey in early childhood education through four comprehensive program areas. From entering the field to advancing in leadership roles, we provide the resources, community, and professional development needed for success.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {programs.map((program) => {
              const Icon = program.icon;
              return (
                <Link 
                  key={program.id} 
                  to={program.link}
                  className="group block"
                >
                  <div className="relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-navy-100 hover:border-brand-gold/30">
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={program.image} 
                        alt={program.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-navy-900/20 to-transparent" />
                      
                      {/* Icon Badge */}
                      <div className={`absolute top-4 left-4 w-14 h-14 rounded-2xl bg-gradient-to-br ${program.color} flex items-center justify-center shadow-lg ${program.shadowColor}`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      
                      {/* Title on Image */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-2xl font-bold text-white">{program.title}</h3>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <p className="text-navy-600 leading-relaxed mb-4">{program.description}</p>
                      <div className="flex items-center text-brand-green font-semibold group-hover:text-green-700 transition-colors">
                        Learn More
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-brand-green to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-green-100 text-lg mb-10">
            Join DC Men in ECE and become part of a movement dedicated to transforming early childhood education in Washington, D.C.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/membership">
              <Button size="lg" className="bg-white text-brand-green font-semibold px-8 hover:bg-green-50 shadow-xl transition-all duration-300">
                Become a Member
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white/30 text-white font-semibold px-8 hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
